/**
 * The new-learner mode default (Aran's ruling via Tom, 2026-08-06; confirmed
 * by Tom 2026-09-28: "new learners start on EASY").
 *
 * EASY IS THE DEFAULT. A learner with NO play history starts on it, and the
 * choice is written to their learner row and this device the moment it is
 * made, so from then on they are a learner who has a mode like any other.
 *
 * The one carve-out: a learner who was already playing before the default
 * landed, and never picked a mode, stays on Fast — the pace they were already
 * learning at — so nobody's course silently slows down underneath them. That is
 * LEGACY_UNCHOSEN_MODE below, and it is also the mode the player holds for the
 * brief moment before saved progress has loaded, because until then "no
 * history" and "not loaded yet" look the same, and guessing Easy would move
 * exactly the learners the carve-out protects.
 *
 * Extracted as a pure function purely so it can be tested — the caller in
 * LearningPlayer.vue owns the refs and the persistence, this owns the decision.
 */
import { DEFAULT_LEARNING_MODE, type LearningMode } from './useAlgorithmConfig'

/** The mode a learner with no history gets handed: THE default. */
export const NEW_LEARNER_DEFAULT_MODE: LearningMode = DEFAULT_LEARNING_MODE

/** Pre-default learners who never chose keep this; also the pre-load hold. */
export const LEGACY_UNCHOSEN_MODE: LearningMode = 'fast'

export interface NewLearnerModeInput {
  /** Has the saved-progress read RESOLVED? Before it has, "no history" and
   *  "not loaded yet" are both empty, and acting on the second would put an
   *  existing learner on Easy — exactly what the ruling forbids. */
  progressResolved: boolean
  /** Has the learner ever picked a mode themselves (learner row or device)? */
  hasChosenMode: boolean
  /** The cursor and in-app minutes are the only learner signals. */
  lastCompletedLegoId?: string | null
  totalPracticeMinutes?: number
}

/** True when this learner shows any evidence of having played before. */
export function hasPlayHistory(input: NewLearnerModeInput): boolean {
  return !!input.lastCompletedLegoId || (input.totalPracticeMinutes ?? 0) > 0
}

/**
 * The mode to apply, or `null` for "leave whatever is already set alone".
 *
 * Returns null — not LEGACY_UNCHOSEN_MODE — for the leave-alone cases, so the
 * caller never writes a mode it did not decide. Overwriting with Fast would
 * clobber a learner whose choice is still in flight from the learner row.
 */
export function resolveNewLearnerMode(input: NewLearnerModeInput): LearningMode | null {
  if (!input.progressResolved) return null   // not loaded yet — decide nothing
  if (input.hasChosenMode) return null       // an explicit choice outranks any default, forever
  if (hasPlayHistory(input)) return null     // pre-default learner — stays on LEGACY_UNCHOSEN_MODE
  return NEW_LEARNER_DEFAULT_MODE
}
